import { GridRowSelectionModel } from "@mui/x-data-grid";
import { useMemo, useState } from "react";
import { MData } from "@/components/MaskMultiSelect";
import { useDebounce } from "@/hooks/use-debounce";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useAddGroupVisitPatients } from "@/queries/group-visit/use-add-group-visit-patients";
import {
  GROUP_VISIT_AVAILABLE_PATIENTS_QUERY_KEY,
  useGroupVisitAvailablePatients,
} from "@/queries/group-visit/use-group-visit-available-patients";
import {
  GROUP_VISIT_PATIENTS_QUERY_KEY,
  useGroupVisitPatients,
} from "@/queries/group-visit/use-group-visit-patients";
import { useRemoveGroupVisitPatient } from "@/queries/group-visit/use-remove-group-visit-patient";
import { useRemoveGroupVisitPatients } from "@/queries/group-visit/use-remove-group-visit-patients";
import { useQueryClient } from "@tanstack/react-query";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
} from "@/utils/utils";
import {
  AvailablePatientRow,
  GroupPatient,
  GroupPatientSummary,
  GroupVisitPatientsResponse,
} from "../group-visit-types";
import { toGroupPatient, toPatientOption } from "../group-visit-utils";
import {
  EMPTY_ROW_SELECTION,
  getSelectedRowCount,
  getSelectedRowIds,
  toRowSelectionModel,
} from "@/utils/data-grid";

type Paged<T> = { content?: T[]; page?: { totalElements?: number } };

/** Options come back either bare or paged, depending on the endpoint. */
const readOptionRows = <T>(payload?: T[] | Paged<T>): T[] =>
  Array.isArray(payload) ? payload : (payload?.content ?? []);

export type UseGroupPatients = {
  patients: GroupPatient[];
  totalCount: number;
  /** Enrolment counts the roster returns alongside its rows. */
  summary?: GroupPatientSummary;
  isLoading: boolean;
  searchKey: string;
  setSearchKey: (value: string) => void;
  pagination: DGT_Pagination;
  setPagination: (value: DGT_Pagination) => void;
  /** Rows ticked for removal. */
  selection: GridRowSelectionModel;
  setSelection: (value: GridRowSelectionModel) => void;
  /** Options for the Add Patient drawer. */
  patientOptions: MData[];
  optionSearchKey: string;
  setOptionSearchKey: (value: string) => void;
  addPatients: (selected: MData[]) => Promise<void>;
  removeSelected: (cancelUpcomingSessions: boolean) => Promise<void>;
  removePatient: (
    patient: GroupPatient,
    cancelUpcomingSessions: boolean
  ) => Promise<void>;
  isRemoving: boolean;
};

/**
 * A group's roster: who is enrolled, who can still be added, and adding or
 * removing them.
 *
 * @author Rajkumar Rathod
 */
export const useGroupPatients = (groupVisitId?: string): UseGroupPatients => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const [debouncedSearch, setSearchKey, searchKey] = useDebounce("");
  const [debouncedOptionSearch, setOptionSearchKey, optionSearchKey] =
    useDebounce("");
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [selection, setSelection] =
    useState<GridRowSelectionModel>(EMPTY_ROW_SELECTION);

  const { data, isLoading } = useGroupVisitPatients<GroupVisitPatientsResponse>(
    {
      groupVisitId,
      pagination,
      searchKey: debouncedSearch.trim() || undefined,
    }
  );

  const { data: availableData } =
    useGroupVisitAvailablePatients<AvailablePatientRow>({
      groupVisitId,
      pagination: DEF_PAGINATION,
      searchKey: debouncedOptionSearch.trim() || undefined,
    });

  const { mutateAsync: addGroupVisitPatients } = useAddGroupVisitPatients();
  const {
    mutateAsync: removeGroupVisitPatients,
    isPending: isRemovingSelected,
  } = useRemoveGroupVisitPatients();
  const { mutateAsync: removeGroupVisitPatient, isPending: isRemovingPatient } =
    useRemoveGroupVisitPatient();

  const roster = data?.data?.patients;

  const patients = useMemo(
    () => (roster?.content ?? []).map(toGroupPatient),
    [roster]
  );

  const patientOptions = useMemo(
    () => readOptionRows(availableData?.data).map(toPatientOption),
    [availableData]
  );

  const refreshRoster = () => {
    queryClient.invalidateQueries({
      queryKey: [GROUP_VISIT_PATIENTS_QUERY_KEY, groupVisitId],
    });
    queryClient.invalidateQueries({
      queryKey: [GROUP_VISIT_AVAILABLE_PATIENTS_QUERY_KEY, groupVisitId],
    });
  };

  const addPatients = async (selected: MData[]) => {
    if (!groupVisitId || !selected.length) return;

    try {
      const res = await addGroupVisitPatients({
        groupVisitId,
        groupVisitPatient: { patients: selected.map((item) => item.id) },
      });

      refreshRoster();
      showSnackbar(getSuccessSnackbar(res));
    } catch (error) {
      showSnackbar(getErrorSnackbar(error));
      throw error;
    }
  };

  const removeSelected = async (cancelUpcomingSessions: boolean) => {
    if (!groupVisitId || !getSelectedRowCount(selection)) return;

    try {
      const selectedIds = new Set(getSelectedRowIds(selection).map(String));
      const patientIds = patients
        .filter((patient) => selectedIds.has(patient.uuid))
        .map((patient) => patient.patientId)
        .filter((id): id is string => !!id);

      const res = await removeGroupVisitPatients({
        groupVisitId,
        groupVisitPatient: { patients: patientIds },
        cancelUpcomingSessions,
      });

      refreshRoster();
      setSelection(EMPTY_ROW_SELECTION);
      showSnackbar(getSuccessSnackbar(res));
    } catch (error) {
      showSnackbar(getErrorSnackbar(error));
      throw error;
    }
  };

  const removePatient = async (
    patient: GroupPatient,
    cancelUpcomingSessions: boolean
  ) => {
    if (!groupVisitId || !patient.patientId) return;

    try {
      const res = await removeGroupVisitPatient({
        groupVisitId,
        patientId: patient.patientId,
        cancelUpcomingSessions,
      });

      refreshRoster();
      setSelection((current) =>
        toRowSelectionModel(
          getSelectedRowIds(current).filter((id) => id !== patient.uuid)
        )
      );
      showSnackbar(getSuccessSnackbar(res));
    } catch (error) {
      showSnackbar(getErrorSnackbar(error));
      throw error;
    }
  };

  return {
    patients,
    totalCount: roster?.page?.totalElements ?? patients.length,
    summary: data?.data?.summary,
    isLoading,
    searchKey,
    setSearchKey,
    pagination,
    setPagination,
    selection,
    setSelection,
    patientOptions,
    optionSearchKey,
    setOptionSearchKey,
    addPatients,
    removeSelected,
    removePatient,
    isRemoving: isRemovingSelected || isRemovingPatient,
  };
};
